/* eslint-disable  @typescript-eslint/no-explicit-any */
import {BehaviorSubject, filter, firstValueFrom, map, Observable, Subject} from 'rxjs';
import type {FilterNode} from '../../../components/result-page/facets-build-wrapper/filter-node';
import type {FilterRequest} from '../../../models/cose-api/request/query-body-request.model';
import type {FacetResponse, FacetsOptionsResponse} from '../../../models/cose-api/response/facet-options.model';
import type {FacetChildUiModel, FacetTreeUiModel} from '../../../models/ui/facets/facet-tree-ui.model';
import {FILTERS_ID_SEPARATOR} from '../../../constants/filterConstants';
import {mergeDeepFilters} from '../../helpers/merge-deep';
import {cloneObjectWithNoReference} from '../../helpers/clone-object-with-no-reference';
import type {EventEmitter} from '@stencil/core';
import type {FacetChange} from '../../../components/result-page/facets/FacetChange';
import {
  FacetsMobileService
} from '../../../components/result-page/facets-build-wrapper/components/mobile-filters-wrapper/services/facets-mobile.service';
import {
  type FiltersSelectedAnalyticsData
} from '../../../components/design-system/mobile-filters/mobile-filters-analytics/filters-selected-mobile.model';
import {createStore, type ObservableMap} from '@stencil/store';
import type {
  DefaultFiltersSelectedAnalyticsData
} from '../../../components/result-page/facets-build-wrapper/components/default-analytics-filters-wrapper/default-filter-selected-events.model';
import {
  FilterChangeContext
} from '../../../components/result-page/facets-build-wrapper/models/filter-change-context.enum';

export type FilterNodeChildRequestUnique = FilterNode;

export interface FilterNodeRequestUnique {
  displayName: string;
  id: string;
  uniqueId: string;
  count: number;
  selected: boolean;
  children: FilterNode[];
  type: string;
}

export class FacetsV2Service {
  key: string;
  facetsState$: BehaviorSubject<FilterNodeRequestUnique[]> = new BehaviorSubject([]);
  facetsRequestState$: BehaviorSubject<FilterRequest[]> = new BehaviorSubject([]);
  facetsOptions$: BehaviorSubject<FacetsOptionsResponse>;
  facetChipsState$: BehaviorSubject<FilterNode[]> = new BehaviorSubject([]);
  onClearFacet$: Subject<FilterNode> = new Subject<FilterNode>();
  chipRemoved$: BehaviorSubject<FilterNode> = new BehaviorSubject(null);
  checkboxesInActiveState$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  accumulatedQueryTree: FilterNodeRequestUnique[];
  skipWatchOnCount = false;
  facetsMobileService: FacetsMobileService;
  notLastUniqueIds: Record<string, string[]> = {};

  defaultAnalyticsStore: ObservableMap<DefaultFiltersSelectedAnalyticsData>;
  mobileAnalyticsStore: ObservableMap<FiltersSelectedAnalyticsData>;
  currentContext: FilterChangeContext = FilterChangeContext.all;

  private static instancesMap: Map<string,FacetsV2Service> = new Map();

  constructor() {
      this.facetsState$ = new BehaviorSubject([]);
      this.facetsOptions$ = new BehaviorSubject(null);
  }

  get facetsState(): Observable<any[]> {
      return this.facetsState$.asObservable();
  }

  get facetsRequestState(): Observable<any[]> {
      return this.facetsRequestState$.asObservable();
  }

  get facetsOptions(): Observable<FacetsOptionsResponse> {
      return this.facetsOptions$.asObservable();
  }

  get facets(): Observable<FacetResponse[]> {
      return this.facetsOptions$
        .pipe(
          filter((fOpt) => !!fOpt),
          map((fOpt:FacetsOptionsResponse) => {
              return fOpt.facets || [];
          }),
        );
  }

  get facetsChipsState(): Observable<FilterNode[]> {
      return this.facetChipsState$;
  }

  get onClearFacet(): Observable<FilterNode> {
      return this.onClearFacet$;
  }

  get classRef(): typeof FacetsV2Service {
      return FacetsV2Service;
  }

  get chipRemoved(): Observable<FilterNode> {
      return this.chipRemoved$.asObservable();
  }

  get checkboxesInActiveState(): Observable<boolean> {
      return this.checkboxesInActiveState$.asObservable();
  }

  setCheckboxesInActiveState(bool: boolean) {
      this.checkboxesInActiveState$.next(bool);
  }

  setFacetsState(facets: FilterNodeRequestUnique[]) {
      this.facetsMobileService.facetsState$.next(facets);
      this.facetsState$.next(facets);
  }

  setFacetsRequestState(facets: FilterNodeRequestUnique[]) {
      let clearFacets = [];
      if(facets.length > 0){
        clearFacets = FacetsV2Service.clearTreeUnique(facets);
      }
      this.facetsMobileService.facetsRequestState$.next(clearFacets);
      this.facetsRequestState$.next(clearFacets);
  }

  setFacets(facets: FacetsOptionsResponse) {
      this.facetsMobileService.facetsOptions$.next(facets);
      this.facetsOptions$.next(facets);
  }

  setFacetsChipsState(filterNodes: FilterNode[]) {
      this.facetsMobileService.facetChipsState$.next(filterNodes);
      this.facetChipsState$.next(filterNodes);
  }

  setChipRemoved(filterNode: FilterNode) {
      filterNode.selected = false;
      this.chipRemoved$.next(filterNode);
  }

  clearStates() {
      this.setFacetsRequestState([]);
      this.setFacetsState([]);
      this.setFacets(null);
      this.setFacetsChipsState([]);
  }

  clearMobileStates() {
      this.facetsMobileService.setFacetsRequestState([]);
      this.facetsMobileService.setFacetsState([]);
      this.facetsMobileService.setFacets(null);
      this.facetsMobileService.setFacetsChipsState([]);
  }

  updateStatesFromMobile() {
      this.facetsState$.next(this.facetsMobileService.facetsState$.value);
      this.facetsRequestState$.next(this.facetsMobileService.facetsRequestState$.value);
  }

  clearFacet(filterNode: FilterNode) {
      this.onClearFacet$.next(filterNode);
  }

  //if selected=true - add to state, if selected=false - remove from state
  addOrRemoveChipOnState(filterNode: FilterNode, selected: boolean): void {
      if(filterNode.uniqueId) {
        const currentFacetChipsState = this.facetChipsState$.value;
        if(selected) {
          const isFacetChipsExists = currentFacetChipsState.some((chips)=> chips.uniqueId === filterNode.uniqueId);
          if(!isFacetChipsExists) {
            currentFacetChipsState.push(filterNode);
          }
        } else {
          const chipsIndex = currentFacetChipsState.findIndex((chips)=> chips.uniqueId === filterNode.uniqueId);
          if(chipsIndex !== -1) {
            currentFacetChipsState.splice(chipsIndex, 1);
          }
        }

        this.setFacetsChipsState(currentFacetChipsState);
      }
  }

  addChipsToState(filterNodes: FilterNode[]): void {
      filterNodes.forEach(filterNode => this.addOrRemoveChipOnState(filterNode, true));
  }

  async createUpdateChipsStateFromResponseTree(tree): Promise<void> {
      const facetsChipsState = await firstValueFrom(this.facetsChipsState);
      const facetsRequestTreeUnique = FacetsV2Service.setUnique([...tree]);

      facetsRequestTreeUnique.forEach((facet) => {
          this.findFilterUpdateChipsState(facet.children, facetsChipsState);
      });
  }

  findFilterUpdateChipsState(filters, facetsChipsState) {
      filters.forEach(filter => {
          if(filter.selected && !facetsChipsState.some(f => f.uniqueId === filter.uniqueId)) {
            this.addOrRemoveChipOnState({
              uniqueId: filter.uniqueId,
              displayName: filter.displayName,
              value: filter.value,
              count: filter.count,
              children: filter.children || [],
              type: filter.type,
              selected: filter.selected
            }, true);
          } else if(!filter.selected && facetsChipsState.some(f => f.uniqueId === filter.uniqueId)) {
            this.addOrRemoveChipOnState({
              uniqueId: filter.uniqueId,
              displayName: filter.displayName,
              value: filter.value,
              count: filter.count,
              children: filter.children || [],
              type: filter.type,
              selected: filter.selected
            }, false);
          }

          if(filter.children.length > 0) {
            this.findFilterUpdateChipsState(filter.children, facetsChipsState);
          }
      });
  }

  onFacetChange(filterNode: FacetChildUiModel, facetsState, facets, searchFacet = false, dividerFacets: string[] = []) {
      const {filterNodeData, tree}
        = FacetsV2Service.facetChangeBeforeBuildQuery(
          {...filterNode},
          facetsState,
          facets,
          searchFacet,
          dividerFacets,
          this.notLastUniqueIds
        );

      this.addOrRemoveChipOnState(filterNodeData as unknown as FilterNode, filterNodeData.selected);
      this.setFacetsState(tree as FilterNodeRequestUnique[]);
      this.setFacetsRequestState(cloneObjectWithNoReference<any>(tree));
  }

  createMobileAnalyticsStore(): ObservableMap<FiltersSelectedAnalyticsData> {
      if(this.mobileAnalyticsStore) {
        return this.mobileAnalyticsStore;
      }

      this.mobileAnalyticsStore = createStore(
        {
          selectedFilters: [],
          deselectedFilters: [],
          selectedEventsList: [],
          deselectedEventsList: []
        }
      );
      return this.mobileAnalyticsStore;
  }

  removeFilterFromMobileStoreByUniqueId(uniqueId: string): void {
      const selectedFilters: FilterNode[] = [...this.mobileAnalyticsStore.get('selectedFilters')];
      const indexSelected = selectedFilters.findIndex(filter => filter.uniqueId === uniqueId);
      selectedFilters.splice(indexSelected, 1);
      this.mobileAnalyticsStore.set( 'selectedFilters', selectedFilters);
  }

  createDefaultAnalyticsStore(): ObservableMap<DefaultFiltersSelectedAnalyticsData> {
      if(this.defaultAnalyticsStore) {
        return this.defaultAnalyticsStore;
      }

      this.defaultAnalyticsStore = createStore(
        {
          selectedFilters: [],
          selectedEventsList: [],
        }
      );
      return this.defaultAnalyticsStore;
  }

  removeFilterFromDefaultStoreByUniqueId(uniqueId: string): void {
      const selectedFilters: FilterNode[] = [...this.defaultAnalyticsStore.get('selectedFilters')];
      const indexSelected = selectedFilters.findIndex(filter => filter.uniqueId === uniqueId);
      selectedFilters.splice(indexSelected, 1);
      this.defaultAnalyticsStore.set( 'selectedFilters', selectedFilters);
  }

  static facetChangeBeforeBuildQuery(
    filterNode: FacetChildUiModel,
    facetsState,
    facets,
    searchFacet = false,
    dividerMobileFacets = [],
    notLastUniqueIds: Record<string, string[]> = {},
    isFlatFacets = false
  ): {filterNodeData: FilterNode, tree:FilterNodeRequestUnique[] } {
      const tree: FilterRequest[] | FilterNodeRequestUnique[] = FacetsV2Service.setUnique(facetsState);

      if (facets && filterNode.selected) {
        let branch: FilterRequest | FilterNodeRequestUnique | any = {};

        cloneObjectWithNoReference(facets).forEach((facet: FacetTreeUiModel) => {
            const facetId = facet.uniqueId.split(FILTERS_ID_SEPARATOR)[0] || facet.id;
            const root = [{ uniqueId: facet.uniqueId, valueId: facetId, isRoot: true }];

            const filters: Array<{ uniqueId: string, valueId: string, isRoot?: boolean }>
          = FacetsV2Service.findFacetRequre(
            facet.children,
            filterNode['uniqueId'],
            facet.uniqueId,
            facet.id,
            root
          );

            const facetRoot = {
              id: filters[0].isRoot ? filters[0].valueId || facetId : undefined,
              uniqueId: filters[0].uniqueId,
              children: []
            };

            // const valuesFilters: Array<{ uniqueId: string, valueId: string }> = filters.slice(facetId ? 2 : 1, filters.length);
            const valuesFilters: Array<{ uniqueId: string, valueId: string }>
              = filters
                .filter(v => v.isRoot !== true)
                .map((v) => {
                    const {uniqueId, valueId} = v;
                    return{uniqueId, valueId};
                });

            // adjustment so that if uniqueId consists of three elements separated by ":", the array is built correctly
            // eslint-disable-next-line no-unsafe-optional-chaining
            if(valuesFilters[0]?.uniqueId && (valuesFilters[0]?.uniqueId).split(FILTERS_ID_SEPARATOR).length === 3) {
              const splittedUniqueId = (valuesFilters[0].uniqueId).split(FILTERS_ID_SEPARATOR);
              const parentFilterValue = {
                uniqueId: splittedUniqueId[0] + FILTERS_ID_SEPARATOR + splittedUniqueId[1],
                valueId: splittedUniqueId[1]
              };
              valuesFilters.unshift(parentFilterValue);
              facetRoot.id = facet.id;
            }

            if (searchFacet) {
              valuesFilters.push({ uniqueId: facetId + FILTERS_ID_SEPARATOR + filterNode.value, valueId: filterNode.value });
            }

            if (facetRoot.id === facet.id && filters.length > 1) {
              const valueTo = FacetsV2Service.arrayToTree(valuesFilters, facetRoot);
              branch = mergeDeepFilters(branch, valueTo);
            }
        });

        if (tree.some(v => v.id === branch['id'])) {
          const index = tree.findIndex(v => v.id === branch['id']);
          if (searchFacet) {
            tree[index].children[0].value = filterNode.value;
          } else {
            tree[index] = mergeDeepFilters(tree[index], { ...branch });
          }
        } else {
          tree.push({ ...branch });
        }
      } else {
        tree.forEach((facet, i) => {
            if(Object.keys(notLastUniqueIds).length <= 0 || !isFlatFacets) {
              FacetsV2Service.findFacetAndDelete(tree[i].children, filterNode.uniqueId, false);
            }

            if(dividerMobileFacets.length > 0) {
              FacetsV2Service.findEmptyDividerFacetsAndDelete(tree[i].children, dividerMobileFacets);
            }

            if(Object.keys(notLastUniqueIds).length > 0 || isFlatFacets) {
              const splitedParentUniqueIds = [];
              filterNode.uniqueId.split(FILTERS_ID_SEPARATOR).slice(0,-1).map((idElement) => {
                  if(splitedParentUniqueIds.length > 0) {
                    splitedParentUniqueIds.push(splitedParentUniqueIds[splitedParentUniqueIds.length - 1] + FILTERS_ID_SEPARATOR + idElement);
                  } else {
                    splitedParentUniqueIds.push(idElement);
                  }
              });

              if(splitedParentUniqueIds.length > 0) {
                splitedParentUniqueIds.forEach((parentUnique) => {
                    const childrenLength = FacetsV2Service.findFilterChildAndHisChildrenLength(tree[i].children, parentUnique);

                    if (childrenLength > 0 || childrenLength === undefined) {
                      FacetsV2Service.findFacetAndDelete(tree[i].children, filterNode.uniqueId, false);
                    } else {
                      FacetsV2Service.findFacetAndDelete(tree[i].children, parentUnique, false);
                    }
                });
              }

            }

            if (tree[i].children.length === 0) {
              tree.splice(i, 1);
            }
        });
      }

      return {filterNodeData: filterNode as FilterNode, tree: tree as FilterNodeRequestUnique[]};
  }

  static findFilterChildAndHisChildrenLength(children, parentUniqueId) {
      let chLength;
      let finded = false;
      children.forEach(ch=> {
          if(ch.uniqueId === parentUniqueId) {
            chLength = ch.children.length;
            finded = true;
          } else if(!finded) {
            chLength = FacetsV2Service.findFilterChildAndHisChildrenLength(ch.children, parentUniqueId);
          }
      });

      return chLength;
  }

  static findFacet(fcs, checkValueUnique, parentUniqueId, parentValue, root, isFinded = false) {
      if(isFinded) {
        return root;
      }
      if(fcs.length > 0) {
        fcs.forEach((f) => {
            if(f['uniqueId'] === checkValueUnique){
              root = [...root,...[{uniqueId: parentUniqueId, valueId: parentValue}],...[{uniqueId: f['uniqueId'], valueId: f['value']}]];
              isFinded = true;
            } else{
              root = FacetsV2Service.findFacet(f.children, checkValueUnique, f['uniqueId'], f['value'], root, isFinded);
            }
        });
      }
      return root;
  };

  static findFacetRequre(fcs, checkValueUnique, parentUniqueId, parentValue, root, isFinded = false) {
      if(isFinded) {
        return root;
      }
      if(fcs.length > 0) {
        fcs.forEach((f) => {
            if(f['uniqueId'] === checkValueUnique) {
              root.push(cloneObjectWithNoReference({uniqueId: f['uniqueId'], valueId: f['value']}));
              isFinded = true;
            } else {
              const splitCheck = checkValueUnique.split(FILTERS_ID_SEPARATOR);
              const splitCurrent = f['uniqueId'].split(FILTERS_ID_SEPARATOR);
              let mustPush = false;

              if(splitCheck.length >= splitCurrent.length) {
                splitCheck.forEach((idCheckEl, i) => {
                    if (
                      splitCurrent.length - 1 <= i
                      && root.filter((rel) => rel.uniqueId === f['uniqueId']).length === 0
                    ) {
                      mustPush = idCheckEl === splitCurrent[i];
                    }
                });
              }

              splitCurrent.forEach((currentEl) => {
                  if(!splitCheck.includes(currentEl)) {
                    mustPush = false;
                  }
              });

              if(mustPush) {
                root.push(cloneObjectWithNoReference({uniqueId: f['uniqueId'], valueId: f['value']}));
              }

              root = FacetsV2Service.findFacetRequre(f.children, checkValueUnique, f['uniqueId'], f['value'], root, isFinded);
            }
        });
      }
      return root;
  };

  static findFacetAndDelete(fcs, checkValueUnique, isFinded) {
      if(isFinded) {
        return;
      }
      if(fcs.length > 0) {
        fcs.forEach((f,i) => {
            if(f['uniqueId'] === checkValueUnique){
              fcs.splice(i,1);
              isFinded = true;
            } else{
              FacetsV2Service.findFacetAndDelete(f.children, checkValueUnique, isFinded);
            }
        });
      }
      return;
  };

  static findEmptyDividerFacetsAndDelete(fcs, dividerMobileFacets = []): void {
      fcs.forEach((f,i) => {
          const facetId = f.uniqueId.split(FILTERS_ID_SEPARATOR)[0];
          if(dividerMobileFacets.includes(facetId) && f.children.length === 0) {
            fcs.splice(i, 1);
          }
      });

      return fcs;
  }

  static flatFactesResponseTree(factes: FacetTreeUiModel[] | FacetChildUiModel[], flatList = []) {

      factes.forEach((facet) => {
          const clearFacet = cloneObjectWithNoReference(facet);
          clearFacet.children.map((f) => {
              f.children = [];
          });
          flatList.push(clearFacet);
          if(facet.children.length > 0) {
            flatList = FacetsV2Service.flatFactesResponseTree(facet.children, flatList);
          }
      });

      return flatList;
  }

  static makeLocalChipsStateForSingleSelectionFromResponse(flatList = []) {
      const chips = [];
      flatList.forEach((fc) => {
          if(fc?.expression && fc?.expression === 'SINGLE') {
            let wasPushed = false;
            const selectedSingle = fc.children.filter((f) => f.selected);
            selectedSingle.forEach((fl) => {
                if(!wasPushed) {
                  chips.push(fl);
                  wasPushed = true;
                }
            });
          }
      });

      return chips;
  }

  static arrayToTree(values: Array<{uniqueId:string,valueId:string}>, facetRoot:{id:string, children:any[]}) {
      let v;
      const changeMakeChild = (part, value) => {
          part.children.push({uniqueId:value.uniqueId, value: value.valueId, children:[]});
          return part.children[0];
      };
      values.forEach((value: {uniqueId:string,valueId:string}, i) => {
          if(i === 0) {
            v = changeMakeChild(facetRoot, value);
          } else{
            v = changeMakeChild(v, value);
          }
      });
      return facetRoot;
  };

  static setUnique(facets, parent = '', i = 0) {
      i++;
      Object.keys(facets).forEach((key) => {
          const inName = parent + ((i === 1) ? '' : FILTERS_ID_SEPARATOR) + facets[key][(i === 1) ? 'id' : 'value'];

          if(facets[key]['uniqueId'] !== inName) {
            if (facets[key]['uniqueId'] && i !== 1) {
              facets[key]['uniqueId'] += inName;
            } else {
              facets[key]['uniqueId'] = inName;
            }
          }

          if(facets[key]['children'] && facets[key]['children'].length > 0) {
            facets[key]['children'] = FacetsV2Service.setUnique(facets[key]['children'], facets[key]['uniqueId'], i);
            if(facets[key]['fieldValues'] && facets[key]['fieldValues'].length > 0) {
              facets[key]['fieldValues'] = FacetsV2Service.setUnique(facets[key]['fieldValues'], facets[key]['uniqueId'], i);
            }
          }
      });

      return facets;
  }

  static setUniqueTest(facets, parent = '', i = 0) {
      i++;
      Object.keys(facets).forEach((key) => {
          const inName = (parent && parent !== undefined ? parent : '') + ((i === 1) ? '' : FILTERS_ID_SEPARATOR) + facets[key][(i === 1) ? 'id' : 'value'];

          if(facets[key]['uniqueId'] !== inName) {
            if (facets[key]['uniqueId'] && i !== 0) {
              facets[key]['uniqueId'] += inName;
            } else {
              facets[key]['uniqueId'] = inName;
            }
          }

          if(facets[key]['children'] && facets[key]['children'].length > 0) {
            facets[key]['children'] = FacetsV2Service.setUniqueTest(facets[key]['children'], facets[key]['uniqueId'], i);
            if(facets[key]['fieldValues'] && facets[key]['fieldValues'].length > 0) {
              facets[key]['fieldValues'] = FacetsV2Service.setUniqueTest(facets[key]['fieldValues'], facets[key]['uniqueId'], i);
            }
          }
      });

      return facets;
  }

  static clearTreeUnique(tree: Array<FilterNodeRequestUnique|FilterNodeChildRequestUnique>): Array<FilterNodeRequestUnique|FilterNodeChildRequestUnique|FilterRequest> {
      tree.map((f) => {
          if(f.uniqueId) {
            delete f.uniqueId;
          }
          if(f.children && f.children.length > 0) {
            f.children = FacetsV2Service.clearTreeUnique(f.children) as Array<FilterNodeChildRequestUnique>;
          }
          return f;
      });

      return tree as FilterRequest[];
  }

  static findFacetByName(
    name: string,
    facetsChildren: FilterNodeChildRequestUnique[],
    idOrValue: string,
    query: FilterNodeRequestUnique | {} = {},
    chipsList: FilterNode[] = []
  ): {currentQuery: FilterNodeRequestUnique | {}, currentChips: FilterNode[]} {
      let wasFind = false;
      facetsChildren.forEach((facetsChild)=> {
          if(facetsChild.value === name && (query as FilterNodeRequestUnique)?.children) {
            (query as FilterNodeRequestUnique).children.push({
              value: facetsChild.value,
              displayName: facetsChild.value,
              selected: facetsChild.selected,
              count: facetsChild.count,
              uniqueId: facetsChild.uniqueId,
              children: facetsChild.children || [],
              type: facetsChild.type,
            });
            chipsList.push({
              value: facetsChild.value,
              displayName: facetsChild.value,
              selected: facetsChild.selected,
              count: facetsChild.count,
              uniqueId: facetsChild.uniqueId,
              children: facetsChild.children || [],
              type: facetsChild.type,
            });
            wasFind = true;
          }
          if(facetsChild.children && !wasFind && query) {
            const {currentQuery, currentChips} = FacetsV2Service.findFacetByName(name, facetsChild.children, facetsChild.value, query, chipsList);
            query = currentQuery;
            chipsList = [...chipsList, ...currentChips];
          }
      });

      return {currentQuery: query, currentChips: chipsList};
  }

  static prepareQueryByNames(names: string[], facets: FilterNodeRequestUnique[]): {currentQueries: FilterNodeRequestUnique[], currentChips: FilterNode[]} {
      const queries: any[] = [];
      let chips: FilterNode[] = [];
      names.forEach((name: string) => {
          facets.forEach((facet) => {
              if(facet.children) {
                const {currentQuery, currentChips} = FacetsV2Service.findFacetByName(name, facet.children, facet.id,{id: facet.id, uniqueId: facet.uniqueId, children: []});
                chips = [...chips, ...currentChips];
                queries.push(currentQuery);
              }
          });
      });

      const filteredQueries: FilterNodeRequestUnique[] = queries.filter((q)=>q.children.length > 0);

      return {currentQueries: FacetsV2Service.mergeArrayQueries(filteredQueries), currentChips: chips};
  }

  static mergeArrayQueries(filteredQueries: FilterNodeRequestUnique[]) {
      const newQ = [];
      filteredQueries.forEach((q) => {
          const index = newQ.findIndex(nq => nq.uniqueId === q.uniqueId);
          if(index === -1) {
            newQ.push(q);
          } else {
            newQ[index] = mergeDeepFilters(newQ[index], q);
          }
      });

      return newQ;
  }

  /**
   * For Exchange and searchTab `setFacetsAndCall` method (not test for MobileFiltering)
   */
  static callfacetChangedEventByRequestArray(emiter: EventEmitter<FacetChange>, query: FilterNodeRequestUnique[], blockQueryCall = true) {
      query.forEach((facet) => {
          facet.children.forEach((filter) => {
              emiter.emit({
                facetType: '',
                facetName: filter.value,
                facetValue: filter.value,
                facetCount: 0,
                facetQuery: '',
                value: filter.value,
                action: 'add',
                id: facet.id,
                uniqueId: filter.uniqueId,
                blockQueryCall
              });
          });
      });
  }

  static getLastChildsFacets(facets?: FacetTreeUiModel[] | FacetChildUiModel[], lastChilds: FacetChildUiModel[] = []) {
      facets.forEach((fc) => {
          if(fc.children.length > 0) {
            const facetFiltersHasChildren = fc.children.filter((f) => f.children.length === 0);
            const isMore = fc.children.every((f) => f.children.length === 0);

            if(isMore) {
              lastChilds.push(fc);
            }

            if(facetFiltersHasChildren && !isMore) {
              facetFiltersHasChildren.forEach((nf) => lastChilds.push(nf));
            }

            FacetsV2Service.getLastChildsFacets(fc.children as FacetChildUiModel[], lastChilds);
          }
      });

      return lastChilds;
  }

  static getNotLastChildsValuesIdsFromUnique(childs: FacetTreeUiModel[] | FacetChildUiModel[]) {
      const notLastUniqueIds: string[] = [];
      childs.forEach((ch) => {
          ch.children.forEach((nestedChilds) => {
              let splitUnique = nestedChilds.uniqueId.split(FILTERS_ID_SEPARATOR);
              splitUnique = splitUnique.slice(0,-1).join(FILTERS_ID_SEPARATOR);
              notLastUniqueIds.push(splitUnique);
          });
      });

      return notLastUniqueIds;
  }

  static getNotLastChildsValuesIdsFromUnique2(childs: FacetTreeUiModel[] | FacetChildUiModel[]) {
      const notLastUniqueIds: Record<string, string[]> = {};
      childs.forEach((ch) => {
          ch.children.forEach((nestedChilds) => {
              if(!notLastUniqueIds[nestedChilds.uniqueId]) {
                notLastUniqueIds[nestedChilds.uniqueId] = [];
              }
              let splitUnique = nestedChilds.uniqueId.split(FILTERS_ID_SEPARATOR);
              splitUnique = splitUnique.slice(0,-1);

              const splitedParentUniqueIds: string[] = [];
              splitUnique.map((idElement) => {
                  if (splitedParentUniqueIds.length > 0) {
                    splitedParentUniqueIds.push(splitedParentUniqueIds[splitedParentUniqueIds.length - 1] + FILTERS_ID_SEPARATOR + idElement);
                  } else {
                    splitedParentUniqueIds.push(idElement);
                  }
              });

              if(splitUnique.length > 0) {
                (notLastUniqueIds[nestedChilds.uniqueId] as string[]).push(...splitedParentUniqueIds);
              }
          });
      });

      return notLastUniqueIds;
  }


  static getSetInstance(key: string) {
      const instance = new this();
      instance.key = key;
      FacetsV2Service.instancesMap.set(key, instance);
      instance.facetsMobileService = FacetsMobileService.getSetInstance(key);
      return instance;
  }

  static getInstance(key: string): FacetsV2Service {
      return FacetsV2Service.instancesMap.has(key) ? FacetsV2Service.instancesMap.get(key) : new this();
  }
}
